import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PiHouseDuotone,
  PiCalendarDuotone,
  PiTrophyDuotone,
  PiArrowRightDuotone,
  PiMagnifyingGlassDuotone,
} from 'react-icons/pi';
import { useAuth } from '../app/providers/AuthContext';
import { getRooms } from '../api/roomService';
import { getEvents } from '../api/eventService';
import Card from '../shared/common/Card';
import Button from '../shared/common/Button';
import StatCard from '../shared/common/StatCard';
import EmptyState from '../shared/common/EmptyState';
import { SkeletonGrid, SkeletonList } from '../shared/common/Skeleton';
import { useDelayedLoading } from '../shared/utils/useDelayedLoading';
import { getEventCategoryLabel } from '../models/eventCategories';
import { publicUserName } from '../shared/utils/userDisplay';
import './DashboardPage.css';

const STAT_EMPTY_VALUE = 0;
const EVENTS_PREVIEW_PAGE = 0;
const EVENTS_PREVIEW_SIZE = 5;
const EVENTS_PREVIEW_PERIOD = 'NEXT_7_DAYS';

const formatEventDate = (event) => {
  const dateStr = event?.eventDate || event?.startDate || event?.endDate || event?.date;
  if (!dateStr) return null;

  return new Date(dateStr).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const sumRoomStat = (rooms, fieldName) => rooms.reduce(
  (total, room) => total + Number(room?.[fieldName] ?? STAT_EMPTY_VALUE),
  STAT_EMPTY_VALUE,
);

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [rooms, setRooms] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(STAT_EMPTY_VALUE);
  const [loading, setLoading] = useState(true);
  const showSkeleton = useDelayedLoading(loading);

  useEffect(() => {
    document.title = 'BetMate — Главная';
    Promise.all([
      getRooms(),
      getEvents({ period: EVENTS_PREVIEW_PERIOD, page: EVENTS_PREVIEW_PAGE, size: EVENTS_PREVIEW_SIZE }),
    ])
      .then(([roomsData, eventsData]) => {
        const eventContent = Array.isArray(eventsData?.content) ? eventsData.content : [];
        setRooms(Array.isArray(roomsData) ? roomsData : []);
        setEvents(eventContent);
        setEventsTotal(Number(eventsData?.totalElements ?? eventContent.length));
      })
      .finally(() => setLoading(false));
  }, []);

  const greeting = publicUserName(user, 'Игрок');
  const winsCount = sumRoomStat(rooms, 'currentUserWinCount');

  return (
    <div className="bm-dashboard bm-animate-in">
      {/* Hero-секция */}
      <section className="bm-dashboard__hero">
        <div>
          <h1 className="bm-h1">Привет, {greeting}! 👋</h1>
          <p className="bm-text-secondary" style={{ marginTop: 6 }}>
            Готов к новым ставкам?
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            variant="secondary"
            size="md"
            icon={<PiMagnifyingGlassDuotone size={16} />}
            onClick={() => navigate('/events/external')}
          >
            Найти матчи
          </Button>
          <Button
            variant="primary"
            size="md"
            icon={<PiHouseDuotone size={16} />}
            onClick={() => navigate('/rooms/create')}
          >
            Создать комнату
          </Button>
        </div>
      </section>

      {/* Статистика */}
      <section className="bm-dashboard__stats">
        <StatCard
          icon={<PiHouseDuotone size={22} />}
          value={loading ? '…' : rooms.length}
          label="Комнат"
          onClick={() => navigate('/rooms')}
          aria-label="Перейти к комнатам"
        />
        <StatCard
          icon={<PiCalendarDuotone size={22} />}
          value={loading ? '…' : eventsTotal}
          label="Событий"
          iconColor="var(--bm-accent-green)"
          iconBg="rgba(0, 214, 143, 0.12)"
          onClick={() => navigate('/events')}
          aria-label="Перейти к событиям"
        />
        <StatCard
          icon={<PiTrophyDuotone size={22} />}
          value={loading ? '…' : winsCount}
          label="Побед"
          iconColor="var(--bm-accent-gold)"
          iconBg="rgba(255, 209, 102, 0.12)"
        />
      </section>

      {/* Мои комнаты */}
      <section className="bm-dashboard__section">
        <div className="bm-dashboard__section-header">
          <h2 className="bm-h3">Мои комнаты</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate('/rooms')}>
            Все комнаты <PiArrowRightDuotone size={14} />
          </Button>
        </div>

        {showSkeleton ? (
          <SkeletonGrid count={3} />
        ) : rooms.length === 0 ? (
          <EmptyState
            icon={<PiHouseDuotone size={40} />}
            title="Комнат пока нет"
            hint="Создай первую комнату и пригласи друзей"
            actionLabel="Создать комнату"
            onAction={() => navigate('/rooms/create')}
          />
        ) : (
          <div className="bm-grid">
            {rooms.slice(0, 6).map((room) => (
              <Card
                key={room.id}
                variant="interactive"
                onClick={() => navigate(`/rooms/${room.id}`)}
              >
                <div className="bm-dashboard__room-icon">🏠</div>
                <h4 className="bm-h4" style={{ marginTop: 12, marginBottom: 4 }}>
                  {room.name}
                </h4>
                {room.description && (
                  <p className="bm-text-secondary bm-text-sm bm-truncate">
                    {room.description}
                  </p>
                )}
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Ближайшие события */}
      <section className="bm-dashboard__section">
        <div className="bm-dashboard__section-header">
          <h2 className="bm-h3">Ближайшие события</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="ghost" size="sm" onClick={() => navigate('/events/external')}>
              Футбольный API <PiArrowRightDuotone size={14} />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/events')}>
              Все события <PiArrowRightDuotone size={14} />
            </Button>
          </div>
        </div>

        {showSkeleton ? (
          <SkeletonList count={3} />
        ) : events.length === 0 ? (
          <EmptyState
            icon={<PiCalendarDuotone size={40} />}
            title="Событий пока нет"
            hint="Создай первое событие вручную или найди футбольный матч"
            actionLabel="Найти матчи"
            onAction={() => navigate('/events/external')}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {events.slice(0, 5).map((event) => {
              const eventDate = formatEventDate(event);
              const categoryLabel = getEventCategoryLabel(event);

              return (
                <Card key={event.id} variant="interactive" onClick={() => navigate(`/events/${event.id}`)} className="bm-dashboard__event-row">
                  <div className="bm-dashboard__event-icon">
                    <PiCalendarDuotone size={18} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p className="bm-font-medium bm-truncate">
                      {event.title || `Событие #${event.id}`}
                    </p>
                    <p className="bm-text-secondary bm-text-sm">
                      {eventDate ? `Начало: ${eventDate}` : 'Дата не указана'}
                    </p>
                    <p className="bm-text-secondary bm-text-sm">
                      Категория: {categoryLabel}
                    </p>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default DashboardPage;
